import {
  Animated,
  Easing,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Colors, Metrics, Typography } from "../../resources";
import { DateRange, Orientation, SIconType } from "../../types";
import { DateRangePicker, DateRangePickerContext } from "@density/ui";
import React, { useEffect, useRef, useState } from "react";
import { useOnclickOutside, useSettings } from "../../hooks";

import { Moment } from "moment-timezone";
import SIcon from "../atoms/SIcon";
import SSpacer from "../atoms/SSpacer";
import { ifElse } from "../../utils";
import moment from "moment";

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

interface IProps {
  week: string;
  isOpen: boolean;
  disabled?: boolean;
  onPress: () => void;
  onSetDate: (date: Moment) => void;
}

const SCalendar: React.FC<IProps> = ({
  week,
  isOpen,
  disabled = false,
  onPress,
  onSetDate,
}) => {
  const [hover, setHover] = useState<boolean>(false);
  const [pressIn, setPressIn] = useState<boolean>(false);
  const [isVisible, setIsvisible] = useState<boolean>(false);

  const currentWeek = () => {
    const selectedDate = moment(week);

    var weekStart = selectedDate.clone().startOf("isoWeek");

    let weekRange: DateRange = {
      startDate: moment(weekStart).add(0, "day").format("YYYY-MM-DD"),
      endDate: moment(weekStart).add(6, "day").format("YYYY-MM-DD"),
    };

    return weekRange;
  };

  const [selectedWeek, setSelectedWeek] = useState<DateRange>(currentWeek());
  const [initialDate, setInitialDate] = useState<Date>(new Date());

  const { writeSetting } = useSettings();

  const onHoverIn = () => setHover(true);
  const onHoverOut = () => setHover(false);
  const onPressIn = () => setPressIn(true);
  const onPressOut = () => setPressIn(false);

  const title = `Week of ${moment(week).format("MMM DD")}`;
  const angleRef = useRef<Animated.Value>(new Animated.Value(0));
  const modalRef = useRef<any>(null);

  useEffect(() => {
    setSelectedWeek(currentWeek());
  }, [week]);

  const _onPress = () => {
    setIsvisible((isVisible) => !isVisible);
  };

  useOnclickOutside(modalRef, _onPress);

  useEffect(() => {
    const stop = isOpen ? 1 : 0;

    Animated.timing(angleRef.current, {
      toValue: stop,
      duration: 350,
      easing: Easing.sin,
      useNativeDriver: true,
    }).start();
  }, [isOpen]);

  const spin = angleRef.current.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "-180deg"],
  });

  const onDayPress = (day: { startDate: any; endDate: any }) => {
    const selectedDate = moment(day.startDate as unknown as string);

    var weekStart = selectedDate.clone().startOf("isoWeek");

    let week = {
      startDate: moment(weekStart).add(0, "day").format("YYYY-MM-DD"),
      endDate: moment(weekStart).add(6, "day").format("YYYY-MM-DD"),
    };

    setSelectedWeek(week);
    writeSetting({
      date: weekStart.format("YYYY-MM-DD"),
    });
    onSetDate(moment(weekStart.format("YYYY-MM-DD")));
  };

  const onCurrentPress = () => {
    const date = new Date();
    setInitialDate(date);
    onDayPress({
      startDate: moment(date).format("YYYY-MM-DD") as unknown as DateRange,
      endDate: moment(date).format("YYYY-MM-DD") as unknown as DateRange,
    });

    onPress();
  };
  const color = ifElse(disabled, Colors.grey300, Colors.blue700);

  return (
    <>
      <Pressable
        style={[
          styles.container,
          hover && !disabled && styles.hover,
          pressIn && !disabled && styles.pressIn,
        ]}
        onPress={_onPress}
        disabled={disabled}
        onMouseEnter={onHoverIn}
        onMouseLeave={onHoverOut}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
      >
        <SSpacer
          type={Orientation.Horizontal}
          space={Metrics.paddingSmallStandard}
        />
        <Text style={[styles.title, { color: color }]}>{title}</Text>
        <Animated.View
          style={[
            styles.chevronContainer,
            {
              transform: [
                {
                  rotate: spin,
                },
              ],
            },
          ]}
        >
          <SIcon
            type={SIconType.ChevronDown}
            size={Metrics.iconSmall}
            color={Colors.blue700}
          />
          <SSpacer
            type={Orientation.Horizontal}
            space={Metrics.paddingSmallStandard}
          />
        </Animated.View>
      </Pressable>
      {isVisible ? (
        <View
          ref={(view) => (modalRef.current = view)}
          style={[styles.modal, { top: 32, right: 125 }]}
        >
          <View style={styles.header}>
            <Text style={styles.select}>SELECT WEEK</Text>
            <TouchableOpacity onPress={onCurrentPress} style={styles.button}>
              <Text style={styles.buttonText}>Current</Text>
            </TouchableOpacity>
          </View>
          <DateRangePickerContext.Provider value="NO_HEADER">
            <DateRangePicker
              onChange={onDayPress}
              onFocusChange={() => {}}
              focusedInput="startDate"
              startDate={selectedWeek.startDate}
              endDate={selectedWeek.endDate}
              numberOfMonths={1}
              commonRanges={[]}
              onSelectCommonRange={() => {}}
              floating={false}
              isOutsideRange={(day) => {
                const dayString = moment(day).format("YYYY-MM-DD");
                return moment(dayString).isAfter(new Date());
              }}
            />
          </DateRangePickerContext.Provider>
        </View>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    height: Metrics.size32,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    backgroundColor: Colors.transparent,
    borderRadius: Metrics.borderRadius6,
    borderWidth: 1,
    borderColor: Colors.grey200,
    // shadowColor: "#03082e",
    // shadowOffset: {
    //   width: 0,
    //   height: 2,
    // },
    // shadowOpacity: 0.2,
    // shadowRadius: 4,
  },
  pressIn: {
    backgroundColor: Colors.midnight10,
  },
  hover: {
    backgroundColor: Colors.midnight05,
  },
  chevronContainer: {
    justifyContent: "center",
    alignItems: "center",
    margin: Metrics.padding6,
  },
  title: {
    ...Typography.body1Medium,
    color: Colors.blue700,
  },
  iconsContainer: {
    marginLeft: Metrics.padding6,
    marginRight: Metrics.padding6,
  },
  modal: {
    zIndex: 100,
    position: "absolute",
    width: 280,
    //height: 300,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.grey200,
    shadowColor: "#03082e",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
    backgroundColor: Colors.white,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: Metrics.size48,
    width: "100%",
    backgroundColor: Colors.grey000,
    borderBottomColor: Colors.grey200,
    borderBottomWidth: 1,
  },
  select: {
    ...Typography.title,
    color: Colors.midnight,
    marginLeft: Metrics.padding16,
  },
  button: {
    height: Metrics.size32,
    width: Metrics.size67,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.white,
    marginRight: Metrics.padding8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.grey200,
    shadowColor: "#03082e",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  buttonText: {
    ...Typography.modalTitle,
    color: Colors.blue700,
  },
});

export default SCalendar;
